import classNames from 'classnames';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import Pagination from '../../../components/Pagination';
import { PintoEbookCollectionsQuery, PintoEbookCollectionStatus } from '../../../core/graphql/types';
import { getDayTime } from '../../../utils/dayjs';

const PAGE_LIMIT = 10;
const DATE_FORMAT = 'DD-MM-YYYY hh:mm A';

export interface IKOLItem {
  id: string;
  title: string;
  description: string;
  status: string;
  updateDate: string;
  coverUrl: string;
  emoji: string;
  hexColorCode: string;
  darkModeHexColorCode: string;
  itemCount: number;
  userAccount: string;
}

export interface IPropsKOLsTablePresenter {
  collectionItems: NonNullable<PintoEbookCollectionsQuery['pintoEbookCollections']>['edges'][0]['node'][];
  currentPage: number;
  pageLimit?: number;
  totalCount?: number;
  onChangePage: (page: number) => void;
}

function KOLsTablePresenter(props: IPropsKOLsTablePresenter) {
  const { collectionItems, currentPage, pageLimit, totalCount, onChangePage } = props;

  const mappedKOLItems = useMemo(
    () =>
      collectionItems.map(
        (item): IKOLItem => ({
          id: item?._id,
          title: item?.title || '',
          description: item?.description || '',
          status: item?.status || '',
          updateDate: item?.updatedAt,
          coverUrl: item?.ebookCoverImageUrls?.[0] || '',
          emoji: item?.emoji || '',
          hexColorCode: item?.hexColorCode || '',
          darkModeHexColorCode: item?.darkModeHexColorCode || '',
          itemCount: item?.itemCount || 0,
          userAccount: item?.user?.displayName || '',
        }),
      ),
    [collectionItems],
  );

  return (
    <div>
      <div className="mt-12 overflow-auto rounded-md">
        <table className="table w-full table-zebra font-tt">
          <thead>
            <tr>
              <th>Title</th>
              <th>KOL</th>
              <th>Description</th>
              <th>Color</th>
              <th>Emoji</th>
              <th>Item Count</th>
              <th>Status</th>
              <th>Update At</th>
            </tr>
          </thead>
          <tbody>
            {mappedKOLItems?.map((item) => (
              <tr key={item.id}>
                <th className="font-normal">
                  <Link to={`/kols/detail?id=${item.id}`} className="underline text-colorsBrandRed01Primary">
                    {item.title}
                  </Link>
                </th>
                <td>{item.userAccount}</td>
                <td className="break-words break-word max-w-[500px] flex-wrap whitespace-pre-wrap">{item.description}</td>
                <td>
                  <div className="inline-flex h-full">
                    <div className="w-6 h-6 rounded-l-8" style={{ backgroundColor: item.hexColorCode }} />
                    <div className="w-6 h-6 rounded-r-8" style={{ backgroundColor: item.darkModeHexColorCode }} />
                  </div>
                </td>
                <td className="text-2xl">{item.emoji}</td>
                <td>{item.itemCount}</td>
                <td>
                  <div
                    className={classNames('badge', {
                      'badge-success': item.status === PintoEbookCollectionStatus.Public,
                      'badge-warning': item.status === PintoEbookCollectionStatus.Draft,
                      'badge-error': item.status === PintoEbookCollectionStatus.Removed,
                      'badge-outline':
                        item.status !== PintoEbookCollectionStatus.Public && item.status !== PintoEbookCollectionStatus.Draft && item.status !== PintoEbookCollectionStatus.Removed,
                    })}
                  >
                    {item.status?.toLocaleUpperCase() || '-'}
                  </div>
                </td>
                <td>{getDayTime(item.updateDate || '', DATE_FORMAT)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-12">
        <span className="text-systemGrays02LabelSecondary">แสดงผล 10 รายการต่อหน้า</span>

        {(totalCount || 0) > 0 && <Pagination currentPage={currentPage} totalCount={totalCount || 0} pageSize={pageLimit || PAGE_LIMIT} onPageChange={onChangePage} />}
      </div>
    </div>
  );
}

export default KOLsTablePresenter;
